import { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { lockCustomer, unlockCustomer } from "../../../api/customer";
import { CUSTOMER_LOCK_REASON_MAX } from "../../../contexts/CustomerLockTracker";

export function CustomerLockDialog({
  customer,
  open,
  onClose,
  onChanged,
}) {
  const { t } = useTranslation();
  const locked = Boolean(customer?.profile_locked);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    setReason("");
    setNote("");
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!customer?.id) return;
    if (!locked && !reason.trim()) {
      toast.error(t("customer.lock.reason_required"));
      return;
    }
    if (!locked && reason.trim().length > CUSTOMER_LOCK_REASON_MAX) {
      toast.error(
        t("customer.lock.reason_too_long", { max: CUSTOMER_LOCK_REASON_MAX })
      );
      return;
    }
    if (locked && note.trim().length > CUSTOMER_LOCK_REASON_MAX) {
      toast.error(
        t("customer.lock.note_too_long", { max: CUSTOMER_LOCK_REASON_MAX })
      );
      return;
    }
    setSaving(true);
    try {
      const data = locked
        ? await unlockCustomer(customer.id, { note: note.trim() })
        : await lockCustomer(customer.id, { reason: reason.trim() });
      toast.success(
        locked ? t("customer.lock.unlock_success") : t("customer.lock.lock_success")
      );
      onChanged?.(data);
      setReason("");
      setNote("");
      onClose?.();
    } catch (err) {
      const message =
        err?.response?.data?.errors?.[0] ||
        err?.errors?.[0] ||
        err?.message ||
        t("customer.lock.save_failed");
      toast.error(typeof message === "string" ? message : t("customer.lock.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title={locked ? t("customer.lock.unlock_title") : t("customer.lock.lock_title")}
      size="md"
    >
      <div className="space-y-4">
        {locked ? (
          <>
            <p className="text-sm text-gray-700">
              {t("customer.lock.unlock_help")}
            </p>
            {customer.lock_reason && (
              <p className="text-sm text-gray-600">
                {t("customer.lock.current_reason", {
                  reason: customer.lock_reason,
                })}
              </p>
            )}
            <label className="block text-sm font-medium text-gray-700">
              {t("customer.lock.unlock_note")}
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={3}
                maxLength={CUSTOMER_LOCK_REASON_MAX}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <span className="mt-1 block text-xs text-gray-500">
                {note.length}/{CUSTOMER_LOCK_REASON_MAX}
              </span>
            </label>
          </>
        ) : (
          <label className="block text-sm font-medium text-gray-700">
            {t("customer.lock.reason_label")}
            <textarea
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={4}
              maxLength={CUSTOMER_LOCK_REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
            <span className="mt-1 block text-xs text-gray-500">
              {reason.length}/{CUSTOMER_LOCK_REASON_MAX}
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className={`rounded-md px-4 py-2 text-sm text-white ${
              locked ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {saving
              ? t("Saving....")
              : locked
                ? t("customer.lock.unlock_action")
                : t("customer.lock.lock_action")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
